import { Stack } from '@startuptools/ui/Stack';
import { Tab, tabClasses } from '@startuptools/ui/Tab';
import { TabList } from '@startuptools/ui/TabList';
import { Tabs } from '@startuptools/ui/Tabs';
import { Link, Navigate, Outlet, useLocation } from 'react-router-dom';
import { MeetingShowInfo } from './ShowInfo';
import { MeetingMinutesWithUploads } from '../common/components/documents/MeetingMinutesWithUploads';
import { Trans } from '@lingui/react';
import { useDeleteMeetingMutation, useMeetingSuspenseQuery } from '../../graphql/react-operations';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { Suspense, useState } from 'react';
import { ShareholdersMeetingsDocument } from '../shareholders-meetings/ShareholdersMeetings.graphql';
import { meetingsLink } from '../../components/Links';
import { AlertConfirmDialog } from '../../components/simple-dialogs/AlertConfirmDialog';
import { useNavigate } from '../../components/Navigate';

export const MeetingShow = ({ meetingId }: { meetingId: string }) => {
  const companyId = useCompanyId();
  const location = useLocation();
  const { navigate } = useNavigate();
  const [showDeleteDialog, setShowDeleteDialog] = useState<string | null>(null);
  const tabValue = location.pathname.split('/').at(-1);
  const {
    data: { meeting },
  } = useMeetingSuspenseQuery({ variables: { companyId, id: meetingId } });
  // eslint-disable-next-line sut/loading-mutation
  const [doDelete] = useDeleteMeetingMutation({ refetchQueries: [ShareholdersMeetingsDocument] });

  if (tabValue === 'show') {
    return <Navigate to="protocol" />;
  }

  return (
    <>
      <Stack gap={2}>
        <MeetingShowInfo meetingId={meetingId} deleteMeeting={meeting => setShowDeleteDialog(meeting.id)} />

        <MeetingMinutesWithUploads meetingId={meetingId} defaultExpanded={!meeting.usesStepper} />

        {meeting.usesStepper && (
          <>
            <Tabs value={tabValue}>
              <TabList
                size="lg"
                disableUnderline
                sx={theme => ({
                  p: 0.5,
                  gap: 0.5,
                  borderRadius: 'lg',
                  bgcolor: 'background.level1',
                  [`& .${tabClasses.root}[aria-selected="true"]`]: {
                    boxShadow: 'sm',
                    bgcolor: 'background.surface',
                    color: theme.palette.primary[500],
                  },
                })}
              >
                <Link to="../protocol" relative="path">
                  <Tab disableIndicator value="protocol">
                    <Trans id="Meeting Minutes" />
                  </Tab>
                </Link>
                <Link to="../participants" relative="path">
                  <Tab disableIndicator value="participants">
                    <Trans id="Participants" />
                  </Tab>
                </Link>
                <Link to="../concerns" relative="path">
                  <Tab disableIndicator value="concerns">
                    <Trans id="Processes" />
                  </Tab>
                </Link>
                <Link to="../todos" relative="path">
                  <Tab disableIndicator value="todos">
                    <Trans id="To-do" />
                  </Tab>
                </Link>
              </TabList>
            </Tabs>

            <Suspense>
              <Outlet />
            </Suspense>
          </>
        )}
      </Stack>
      {showDeleteDialog && (
        <AlertConfirmDialog
          title={<Trans id="Remove Meeting" />}
          onClose={() => setShowDeleteDialog(null)}
          content={<Trans id="Are you sure you want to delete this meeting?" />}
          onConfirm={async () => {
            await doDelete({ variables: { companyId, input: { id: showDeleteDialog } } });
            await navigate(meetingsLink(meeting.kind));
            setShowDeleteDialog(null);
          }}
        />
      )}
    </>
  );
};
